import { useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { DailyQuestView } from '../../../models/quest';
import type { NavigationConfirmDestination } from '../types';

export interface UseDailyQuestUiStateResult {
  questRunState: DailyQuestRunState;
  xTimestamps: DailyQuestXTimestamps;
  xModals: DailyQuestXModals;
  questList: DailyQuestListState;
  taskAd: DailyQuestTaskAdState;
  navigationConfirm: DailyQuestNavigationConfirmState;
  vipInfo: DailyQuestVipInfoState;
}

export interface DailyQuestRunState {
  claimingQuestIds: Set<number>;
  setClaimingQuestIds: Dispatch<SetStateAction<Set<number>>>;
  showingAdQuestIds: Set<number>;
  setShowingAdQuestIds: Dispatch<SetStateAction<Set<number>>>;
  claimedQuestIds: Set<number>;
  setClaimedQuestIds: Dispatch<SetStateAction<Set<number>>>;
  startedVerificationQuestIds: Set<number>;
  setStartedVerificationQuestIds: Dispatch<SetStateAction<Set<number>>>;
  isVerifyingQuest: number | null;
  setIsVerifyingQuest: Dispatch<SetStateAction<number | null>>;
}

export interface DailyQuestXTimestamps {
  xQuestModerationTimestamps: Record<number, number>;
  setXQuestModerationTimestamps: Dispatch<SetStateAction<Record<number, number>>>;
  xQuestVerificationStartTimestamps: Record<number, number>;
  setXQuestVerificationStartTimestamps: Dispatch<SetStateAction<Record<number, number>>>;
  xQuestErrorTimestamps: Record<number, number>;
  setXQuestErrorTimestamps: Dispatch<SetStateAction<Record<number, number>>>;
}

export interface DailyQuestXModals {
  showXVerificationLoadingModal: boolean;
  _setShowXVerificationLoadingModal: Dispatch<SetStateAction<boolean>>;
  showXVerificationErrorModal: boolean;
  setShowXVerificationErrorModal: Dispatch<SetStateAction<boolean>>;
  showXVerificationSuccessModal: boolean;
  setShowXVerificationSuccessModal: Dispatch<SetStateAction<boolean>>;
  showModerationInfoModal: boolean;
  setShowModerationInfoModal: Dispatch<SetStateAction<boolean>>;
  activeXModalQuestId: number;
  setActiveXModalQuestId: Dispatch<SetStateAction<number>>;
}

export interface DailyQuestListState {
  sortedQuests: DailyQuestView[];
  setSortedQuests: Dispatch<SetStateAction<DailyQuestView[]>>;
}

export interface DailyQuestTaskAdState {
  taskAdAvailable: boolean;
  setTaskAdAvailable: Dispatch<SetStateAction<boolean>>;
}

export interface DailyQuestNavigationConfirmState {
  navigationConfirmModal: {
    isOpen: boolean;
    destination: NavigationConfirmDestination;
  };
  setNavigationConfirmModal: Dispatch<SetStateAction<{
    isOpen: boolean;
    destination: NavigationConfirmDestination;
  }>>;
}

export interface DailyQuestVipInfoState {
  showVipInfoModal: boolean;
  setShowVipInfoModal: Dispatch<SetStateAction<boolean>>;
}

export function useDailyQuestUiState(): UseDailyQuestUiStateResult {
  const [claimingQuestIds, setClaimingQuestIds] = useState<Set<number>>(new Set());
  // Відстежуємо, які квести зараз показують рекламу (для блокування кнопки)
  const [showingAdQuestIds, setShowingAdQuestIds] = useState<Set<number>>(new Set());
  // Відстежуємо, які квести вже мають забрану нагороду
  const [claimedQuestIds, setClaimedQuestIds] = useState<Set<number>>(new Set());
  // Відстежуємо, які квести (6, 7, 108) мають натиснуту кнопку START (щоб показувати "Перевірити")
  // Стан зберігається в CloudStorage для синхронізації між пристроями
  const [startedVerificationQuestIds, setStartedVerificationQuestIds] = useState<Set<number>>(new Set());
  const [isVerifyingQuest, setIsVerifyingQuest] = useState<number | null>(null); // userQuestId квесту, який зараз перевіряється
  const [xQuestModerationTimestamps, setXQuestModerationTimestamps] = useState<Record<number, number>>({});
  const [xQuestVerificationStartTimestamps, setXQuestVerificationStartTimestamps] = useState<Record<number, number>>({});
  const [xQuestErrorTimestamps, setXQuestErrorTimestamps] = useState<Record<number, number>>({});
  // Стан для показу модального вікна з лоадером перевірки підписки на X
  const [showXVerificationLoadingModal, _setShowXVerificationLoadingModal] = useState(false);
  // Стан для показу модального вікна з помилкою перевірки підписки на X
  const [showXVerificationErrorModal, setShowXVerificationErrorModal] = useState(false);
  // Стан для показу модального вікна з успішним повідомленням перевірки підписки на X
  const [showXVerificationSuccessModal, setShowXVerificationSuccessModal] = useState(false);
  // Зберігаємо відсортований список квестів, щоб він не "стрибав" під час перегляду
  const [sortedQuests, setSortedQuests] = useState<DailyQuestView[]>([]);
  // Стан для показу модального вікна з інформацією про модерацію квесту підписки на X
  const [showModerationInfoModal, setShowModerationInfoModal] = useState(false);
  // Який саме X квест зараз активний для модалок (помилка, модерація, успіх)
  const [activeXModalQuestId, setActiveXModalQuestId] = useState<number>(208);
  const [taskAdAvailable, setTaskAdAvailable] = useState(false);
  const [navigationConfirmModal, setNavigationConfirmModal] = useState<{
    isOpen: boolean;
    destination: NavigationConfirmDestination;
  }>({ isOpen: false, destination: 'shop' });
  const [showVipInfoModal, setShowVipInfoModal] = useState(false);

  return {
    questRunState: {
      claimingQuestIds,
      setClaimingQuestIds,
      showingAdQuestIds,
      setShowingAdQuestIds,
      claimedQuestIds,
      setClaimedQuestIds,
      startedVerificationQuestIds,
      setStartedVerificationQuestIds,
      isVerifyingQuest,
      setIsVerifyingQuest,
    },
    xTimestamps: {
      xQuestModerationTimestamps,
      setXQuestModerationTimestamps,
      xQuestVerificationStartTimestamps,
      setXQuestVerificationStartTimestamps,
      xQuestErrorTimestamps,
      setXQuestErrorTimestamps,
    },
    xModals: {
      showXVerificationLoadingModal,
      _setShowXVerificationLoadingModal,
      showXVerificationErrorModal,
      setShowXVerificationErrorModal,
      showXVerificationSuccessModal,
      setShowXVerificationSuccessModal,
      showModerationInfoModal,
      setShowModerationInfoModal,
      activeXModalQuestId,
      setActiveXModalQuestId,
    },
    questList: {
      sortedQuests,
      setSortedQuests,
    },
    taskAd: {
      taskAdAvailable,
      setTaskAdAvailable,
    },
    navigationConfirm: {
      navigationConfirmModal,
      setNavigationConfirmModal,
    },
    vipInfo: {
      showVipInfoModal,
      setShowVipInfoModal,
    },
  };
}
